'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function KronikBasAgrisiTedavisindeBotoks() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/kronik-bas-agrisi-tedavisinde-botoks';
  const pageTitle = 'Kronik Baş Ağrısı Tedavisinde Botoks';
  const pageDescription = 'Kronik baş ağrısı tedavisinde botoks etkili midir? Botulinum toksin ile baş ağrısı tedavisi, uygulama alanları ve etki mekanizması.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Kronik Baş Ağrısı Tedavisinde Botoks', href: '/tr/blog/kronik-bas-agrisi-tedavisinde-botoks' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Kronik baş ağrısı tedavisinde botoks uygulaması"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-indigo-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Tıbbi Tedavi
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Kronik Baş Ağrısı Tedavisinde Botoks
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>15 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Kronik baş ağrısı, günlük yaşamı ciddi şekilde etkileyen ve yaşam kalitesini düşüren bir sağlık sorunudur.
                Geleneksel tedavi yöntemlerine yanıt vermeyen hastalarda botulinum toksin (botoks) etkili bir alternatif olabilir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu yazıda kronik baş ağrısı tedavisinde botoksun nasıl kullanıldığını, etki mekanizmasını ve başarı oranlarını inceleyeceğiz.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Kronik Baş Ağrısı Nedir?
              </h2>
              
              <div className="bg-indigo-50 rounded-xl p-6 mb-6">
                <p className="text-indigo-800 mb-4 font-medium">
                  Kronik baş ağrısı, ayda 15 gün veya daha fazla süren, en az 3 ay devam eden baş ağrısı olarak tanımlanır.
                  Bu durum migren, gerilim tipi baş ağrısı veya karma baş ağrısı sendromu olabilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Botoksun Baş Ağrısındaki Etki Mekanizması
              </h2>
              
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 mb-4">
                  Botulinum toksin, kas kasılmalarını engelleyerek baş ağrısına neden olan gerginliği azaltır.
                  Ayrıca sinir uçlarından ağrı sinyallerinin iletimini de engelleyebilir.
                </p>
                <div className="bg-blue-100 rounded-lg p-4 mt-4">
                  <p className="text-blue-900 font-medium">
                    💡 Botoks, hem kas gevşetici hem de ağrı sinyali engelleyici olarak çalışır.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Uygulama Alanları
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <p className="text-green-800 mb-4 font-medium">
                  Botoks baş ağrısı tedavisinde şu bölgelere uygulanır:
                </p>
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>Alın bölgesi</li>
                  <li>Kaş arası (glabella)</li>
                  <li>Şakak bölgesi</li>
                  <li>Boyun ve omuz kasları</li>
                  <li>Çene kasları</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Tedavi Protokolü
              </h2>
              
              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <p className="text-purple-800 mb-4">
                  Kronik baş ağrısı tedavisinde botoks genellikle 12 hafta aralıklarla uygulanır.
                  İlk etki 2-4 hafta içinde görülmeye başlar ve 3-4 ay sürer.
                </p>
                <div className="bg-purple-100 rounded-lg p-4 mt-4">
                  <p className="text-purple-900 font-medium">
                    ⏰ Tedavi süreci: 3-4 seans, 12 hafta aralıklarla
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Başarı Oranları
              </h2>
              
              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <p className="text-yellow-800 mb-4 font-medium">
                  Çalışmalara göre kronik migren hastalarının %50-70'inde baş ağrısı sıklığı ve şiddetinde belirgin azalma görülür.
                  Ağrı kesici kullanımında da önemli düşüş sağlanır.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-yellow-100 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-900 mb-2">Migren Hastaları</h4>
                    <p className="text-yellow-800">%50-70 başarı oranı</p>
                  </div>
                  <div className="bg-yellow-100 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-900 mb-2">Gerilim Tipi</h4>
                    <p className="text-yellow-800">%40-60 başarı oranı</p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Kimler İçin Uygundur?
              </h2>
              
              <div className="bg-pink-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-3 text-pink-800">
                  <li>Kronik migren hastaları</li>
                  <li>Gerilim tipi baş ağrısı olanlar</li>
                  <li>Geleneksel tedavilere yanıt vermeyen hastalar</li>
                  <li>Ağrı kesici bağımlılığı gelişen kişiler</li>
                </ul>
                <div className="bg-pink-100 rounded-lg p-4 mt-4">
                  <p className="text-pink-900 font-medium">
                    ⚠️ Hamilelik ve emzirme döneminde uygulanmaz.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Botoks baş ağrısını tamamen geçirir mi?</h3>
                  <p className="text-gray-600">Tamamen geçirmese de sıklığını ve şiddetini önemli ölçüde azaltır.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Ne kadar sürede etki eder?</h3>
                  <p className="text-gray-600">İlk etki 2-4 hafta içinde görülmeye başlar, tam etki 3-4 ay sürer.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Hangi baş ağrısı türlerinde etkilidir?</h3>
                  <p className="text-gray-600">Kronik migren, gerilim tipi baş ağrısı ve karma baş ağrısı sendromunda etkilidir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Yan etkileri nelerdir?</h3>
                  <p className="text-gray-600">Enjeksiyon bölgesinde geçici ağrı, kızarıklık ve nadiren kas zayıflığı olabilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>American Headache Society – "Botulinum Toxin for Chronic Migraine"</li>
                  <li>Mayo Clinic – "Botox for Chronic Migraine Treatment"</li>
                  <li>Harvard Health Publishing – "Botox for Headaches: What You Need to Know"</li>
                  <li>Neurology Journal – "Efficacy of Botulinum Toxin in Chronic Headache"</li>
                  <li>Türk Nöroloji Derneği – "Kronik Baş Ağrısı Tedavi Kılavuzu"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#kronik baş ağrısı</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#botoks baş ağrısı</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#migren tedavisi</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#baş ağrısı tedavisi</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#botulinum toksin</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#nöroloji</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-indigo-600 to-blue-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Kronik Baş Ağrısı Tedavisi İçin Randevu Alın</h3>
          <p className="mb-6">Uzman doktorumuzdan detaylı bilgi alın ve size özel değerlendirme için hemen iletişime geçin.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Kronik baş ağrısı tedavisi hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-indigo-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/migren-tedavisinde-botoks-bilimsel-ve-klinik-yaklasim"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Migren Tedavisinde Botoks</h4>
              <p className="text-gray-600 text-sm">Migren tedavisinde botoksun bilimsel yaklaşımı ve klinik uygulamaları.</p>
            </a>
            <a
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir, Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoksun ne olduğu, nasıl çalıştığı ve uygulama süreci hakkında detaylı bilgiler.</p>
            </a>
            <a
              href="/tr/blog/botoks-etkisinin-suresi-ne-kadar-kalicidir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Etkisinin Süresi</h4>
              <p className="text-gray-600 text-sm">Botoksun ne kadar süre etkili olduğu ve kalıcılık faktörleri.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}